import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Reveal } from '../hooks/useReveal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    setError(false);
    try {
      await axios.post(`${API}/contact`, { ...form, language });
      setSent(true);
      setForm({ name: '', email: '', phone: '', company: '', message: '' });
      setTimeout(() => setSent(false), 5000);
    } catch (_err) {
      setError(true);
      setTimeout(() => setError(false), 5000);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#05060A] grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt</p>
          <h1 data-testid="contact-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-4">
            {t('contact_section.label')}
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-xl">{t('contact_section.subtitle')}</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Info */}
            <div className="lg:col-span-4">
              <Reveal>
                <div className="space-y-8">
                  <div className="border border-white/10 bg-[#05060A] p-6 hover:border-blue-600/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('contact_section.address')}</p>
                        <p className="text-sm text-white font-medium">{t('contact_section.factory')}</p>
                        <p className="text-sm text-slate-400 mt-1">Koc Oren Mah. 2. Sokak No:29 Organize Sanayi Sitesi Eyyubiye / Sanliurfa</p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-white/10 bg-[#05060A] p-6 hover:border-blue-600/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('contact_section.phone_label')}</p>
                        <p className="text-sm text-white font-medium">+90 543 211 52 83</p>
                        <p className="text-xs text-slate-500 mt-1">WhatsApp: +90 543 211 52 83</p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-white/10 bg-[#05060A] p-6 hover:border-blue-600/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('contact_section.email_label')}</p>
                        <p className="text-sm text-white font-medium">info@astravolttrafo.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              <Reveal>
                <div className="border border-white/10 bg-[#05060A] p-8 md:p-12">
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-8">
                    {t('contact_section.title')}
                  </h2>

                  {sent && (
                    <div data-testid="contact-success" className="flex items-center gap-3 p-4 bg-green-600/10 border border-green-600/20 text-green-400 text-sm mb-6 rounded-sm">
                      <CheckCircle className="w-5 h-5" />
                      {t('contact_section.success')}
                    </div>
                  )}

                  {error && (
                    <div data-testid="contact-error" className="flex items-center gap-3 p-4 bg-red-600/10 border border-red-600/20 text-red-400 text-sm mb-6 rounded-sm">
                      <Send className="w-5 h-5" />
                      An error occurred. Please try again.
                    </div>
                  )}

                  <p className="text-xs text-slate-500 mb-6 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-blue-400" />
                    <span>info@astravolttrafo.com</span>
                  </p>

                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-xs text-slate-400 uppercase tracking-wider">
                          {t('contact_section.name')} *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          data-testid="contact-name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          className="bg-[#0B0E14] border-white/10 text-white rounded-sm h-12 focus:border-blue-600 focus:ring-blue-600/20"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs text-slate-400 uppercase tracking-wider">
                          {t('contact_section.email')} *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="contact-email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          className="bg-[#0B0E14] border-white/10 text-white rounded-sm h-12 focus:border-blue-600 focus:ring-blue-600/20"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-xs text-slate-400 uppercase tracking-wider">
                          {t('contact_section.phone')}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          data-testid="contact-phone"
                          value={form.phone}
                          onChange={handleChange}
                          className="bg-[#0B0E14] border-white/10 text-white rounded-sm h-12 focus:border-blue-600 focus:ring-blue-600/20"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-xs text-slate-400 uppercase tracking-wider">
                          {t('contact_section.company')}
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          data-testid="contact-company"
                          value={form.company}
                          onChange={handleChange}
                          className="bg-[#0B0E14] border-white/10 text-white rounded-sm h-12 focus:border-blue-600 focus:ring-blue-600/20"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-xs text-slate-400 uppercase tracking-wider">
                        {t('contact_section.message')} *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="contact-message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-[#0B0E14] border-white/10 text-white rounded-sm focus:border-blue-600 focus:ring-blue-600/20 resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={sending}
                      className="bg-blue-600 text-white rounded-sm px-10 py-5 font-bold uppercase tracking-widest hover:bg-blue-500 transition-colors text-sm disabled:opacity-50"
                    >
                      {sending ? '...' : t('contact_section.send')}
                      <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
